/* =============================================================================
   Cascading Option (Mix & Match) — JS
   Ported from Arktic Studio's product-form custom_option implementation.
   Self-contained: defines its own <cascading-combo> web component, bootstraps
   the cascading dependency + image-swap behavior, and hooks into Wonder's
   <product-form> submit pipeline for validation.

   Mobile fix: combo handlers are bound ONCE in the constructor. When the theme
   relocates the product-info column at the mobile breakpoint the element
   disconnects + reconnects, which re-ran connectedCallback and — because the
   old code used `.bind(this)` (a fresh reference every call) — stacked a second
   toggle listener on the trigger. One tap then fired open+close in the same
   frame, so on mobile Safari the dropdown "wouldn't open". Stable references
   make reconnection idempotent; document listeners are torn down on disconnect.
   ============================================================================= */

(function () {
  "use strict";

  /* ---------------------------- <cascading-combo> --------------------------- */

  if (!customElements.get("cascading-combo")) {
    customElements.define(
      "cascading-combo",
      class CascadingCombo extends HTMLElement {
        constructor() {
          super();

          this._onTriggerClick = () => {
            this.setAttribute(
              "aria-expanded",
              this.getAttribute("aria-expanded") === "true" ? "false" : "true"
            );
          };

          this._onSelect = (e) => {
            const item = e.currentTarget;
            if (item.disabled) return;
            const value = item.getAttribute("value");

            this.items.forEach((i) =>
              i.setAttribute("aria-selected", i === item ? "true" : "false")
            );

            if (this.labelEl) {
              this.labelEl.textContent = value;
              this.labelEl.classList.remove("co-combo__trigger__placeholder");
            }

            this.classList.add("is-filled");

            if (this.select) {
              this.select.value = value;
              this.select.dispatchEvent(new Event("change", { bubbles: true }));
            }

            this._close();
          };

          // pointerdown (not click) — iOS Safari doesn't deliver clicks on
          // non-interactive elements to document-level listeners, so tap-away
          // close was dead on iPhone. pointerdown also fires before the
          // trigger's click, so the ordering is deterministic without any
          // stopPropagation, and opening one combo now closes any other.
          this._onOutsidePointerDown = (e) => {
            if (
              this.getAttribute("aria-expanded") === "true" &&
              !this.contains(e.target)
            ) {
              this._close();
            }
          };

          this._onKeydown = (e) => {
            if (e.key === "Escape") this._close();
          };
        }

        connectedCallback() {
          this.trigger = this.querySelector(".co-combo__trigger");
          this.items = Array.from(this.querySelectorAll(".co-combo__item"));
          this.select =
            this.parentElement && this.parentElement.querySelector("select");
          this.labelEl = this.querySelector(".co-combo__trigger__label");
          this.defaultLabel = this.labelEl ? this.labelEl.textContent : "";

          if (this.getAttribute("aria-expanded") !== "true") {
            this.setAttribute("aria-expanded", "false");
          }

          if (this.trigger) {
            this.trigger.addEventListener("click", this._onTriggerClick);
          }
          this.items.forEach((item) =>
            item.addEventListener("click", this._onSelect)
          );
          document.addEventListener("pointerdown", this._onOutsidePointerDown);
          document.addEventListener("keydown", this._onKeydown);
        }

        disconnectedCallback() {
          document.removeEventListener(
            "pointerdown",
            this._onOutsidePointerDown
          );
          document.removeEventListener("keydown", this._onKeydown);
        }

        _close() {
          this.setAttribute("aria-expanded", "false");
        }
      }
    );
  }

  /* ------------------------ Image swap / cascade engine ---------------------- */
  /* The active "main" media slide is the first <li data-swiper-slide> inside
     <ul data-swiper-container>. We append <img.co-overlay-image> over it; CSS
     positions it absolutely on top of wt-product__img. Overlays are cleared on
     each change. */

  function getActiveSlide() {
    return document.querySelector(
      "[data-swiper-container] [data-swiper-slide]"
    );
  }

  function clearOverlays(slide) {
    slide
      .querySelectorAll(".co-overlay-image, .co-image-loading")
      .forEach((n) => n.parentNode.removeChild(n));
  }

  function appendLoader(slide) {
    const loading = document.createElement("div");
    loading.className = "co-image-loading";
    const loader = document.createElement("span");
    loader.className = "co-loader";
    loading.appendChild(loader);
    slide.appendChild(loading);
    return loading;
  }

  function appendOverlay(slide, src, idClass) {
    const existing = slide.querySelector(
      '.co-overlay-image[src="' + CSS.escape(src) + '"]'
    );
    if (existing) return existing;

    const img = document.createElement("img");
    img.src = src;
    img.className = "co-overlay-image is-loading " + (idClass || "");
    slide.appendChild(img);

    img.onload = () => {
      setTimeout(() => img.classList.remove("is-loading"), 250);
    };

    return img;
  }

  function removeLoaderIfDone(slide) {
    const stillLoading = slide.querySelectorAll(".co-overlay-image.is-loading");
    if (stillLoading.length === 0) {
      const loader = slide.querySelector(".co-image-loading");
      if (loader) loader.parentNode.removeChild(loader);
    }
  }

  /* ---------------------------- Bootstrap per page --------------------------- */

  function init(root) {
    root = root || document;
    const options = root.querySelectorAll(".co-option");
    if (options.length === 0) return;

    // Metaobject-derived paired-image map (material + color -> image), emitted
    // from main-product.liquid alongside the cascading_option blocks.
    let groupedMap = {};
    try {
      const scriptEl = root.querySelector("script[data-cascading-options-map]");
      if (scriptEl && scriptEl.textContent.trim()) {
        groupedMap = JSON.parse(scriptEl.textContent);
      }
    } catch (e) {
      console.warn("[cascading-option] Could not parse paired-image map", e);
    }

    let imageSwapDebounce;

    function applyImagesForCurrentState() {
      const slide = getActiveSlide();
      if (!slide) return;
      clearOverlays(slide);

      const selections = [];
      options.forEach((option) => {
        const handle = option.dataset.handle;

        // Block (radio) mode
        const checkedRadio = option.querySelector(
          'input[type="radio"]:checked'
        );
        if (checkedRadio) {
          selections.push({
            optionHandle: handle,
            valueHandle: checkedRadio.dataset.handle,
            element: option,
          });
          return;
        }

        // Dropdown mode
        const combo = option.querySelector("cascading-combo.is-filled");
        if (combo) {
          const selected = combo.querySelector(
            '.co-combo__item[aria-selected="true"]'
          );
          if (selected) {
            selections.push({
              optionHandle: handle,
              valueHandle: selected.dataset.handle,
              element: option,
              selectedItem: selected,
            });
          }
        }
      });

      if (selections.length === 0) return;

      appendLoader(slide);

      selections.forEach((sel) => {
        let pairedImage = "";
        let pairedKey = "";
        const selfKey = sel.optionHandle + "--" + sel.valueHandle;

        Object.keys(groupedMap).forEach((key) => {
          if (key.indexOf(selfKey) === -1) return;
          const otherKey = key.replace(selfKey, "").replace("__", "");
          const [otherOpt, otherVal] = otherKey.split("--");

          const otherSelected = selections.find(
            (s) => s.optionHandle === otherOpt && s.valueHandle === otherVal
          );
          if (otherSelected) {
            pairedImage = groupedMap[key];
            pairedKey = key;
          }
        });

        if (pairedImage) {
          appendOverlay(slide, pairedImage, "co-overlay-pair-" + pairedKey);
        } else {
          if (sel.selectedItem) {
            const img = sel.selectedItem.querySelector("img");
            const src = img && (img.dataset.src || img.src);
            if (src) appendOverlay(slide, src, "co-overlay-" + selfKey);
          } else {
            const radio = sel.element.querySelector(
              'input[type="radio"]:checked'
            );
            const wrapper = radio && radio.closest(".co-swatch");
            const src = wrapper && wrapper.dataset.defaultImage;
            if (src) appendOverlay(slide, src, "co-overlay-" + selfKey);
          }
        }
      });

      const finishTimer = setInterval(() => {
        removeLoaderIfDone(slide);
        if (!slide.querySelector(".co-image-loading")) {
          clearInterval(finishTimer);
        }
      }, 250);
      setTimeout(() => clearInterval(finishTimer), 5000);
    }

    function scheduleImageSwap() {
      clearTimeout(imageSwapDebounce);
      imageSwapDebounce = setTimeout(applyImagesForCurrentState, 80);
    }

    function clearError(option) {
      option.querySelectorAll(".co-error").forEach((e) => e.remove());
    }

    function applyCascade(currentOption, nextValues) {
      const next = currentOption.nextElementSibling;
      if (!next || !next.classList.contains("co-option")) return;

      const allowed = (nextValues || "")
        .toLowerCase()
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      const noRestriction = allowed.length === 0;

      // Block-swatch radios
      next.querySelectorAll(".co-swatch").forEach((wrapper) => {
        const value = wrapper.dataset.optionValue &&
          wrapper.dataset.optionValue.toLowerCase();
        const radio = wrapper.querySelector('input[type="radio"]');
        if (noRestriction || allowed.includes(value)) {
          wrapper.classList.remove("is-disabled");
          if (radio) radio.disabled = false;
        } else {
          wrapper.classList.add("is-disabled");
          if (radio) {
            if (radio.checked) radio.checked = false;
            radio.disabled = true;
          }
        }
      });

      // Combo-box items + hidden <select>
      next.querySelectorAll(".co-combo__item").forEach((btn) => {
        const value =
          btn.getAttribute("value") &&
          btn.getAttribute("value").toLowerCase();
        if (noRestriction || allowed.includes(value)) {
          btn.disabled = false;
          btn.hidden = false;
        } else {
          btn.disabled = true;
          btn.hidden = true;
          if (btn.getAttribute("aria-selected") === "true") {
            btn.setAttribute("aria-selected", "false");
            const combo = btn.closest("cascading-combo");
            if (combo) combo.classList.remove("is-filled");
            const label =
              combo && combo.querySelector(".co-combo__trigger__label");
            if (label && combo) {
              label.textContent =
                combo.dataset.defaultLabel || combo.defaultLabel || "";
              label.classList.add("co-combo__trigger__placeholder");
            }
            const sel = next.querySelector("select");
            if (sel) sel.value = "";
          }
        }
      });
    }

    options.forEach((option) => {
      option.querySelectorAll('input[type="radio"]').forEach((radio) => {
        radio.addEventListener("change", () => {
          clearError(option);
          const wrapper = radio.closest(".co-swatch");
          applyCascade(option, (wrapper && wrapper.dataset.nextOptionValues) || "");
          scheduleImageSwap();
        });
      });

      const select = option.querySelector("select");
      if (select) {
        select.addEventListener("change", () => {
          clearError(option);
          const chosen = select.querySelector(
            'option[value="' + select.value.replace(/"/g, '\\"') + '"]'
          );
          applyCascade(option, (chosen && chosen.dataset.nextOptionValues) || "");
          scheduleImageSwap();
        });
      }
    });
  }

  /* ----------------------- Submit-time validation hook ----------------------- */
  /* Capturing listener so we can preventDefault before <product-form>'s own
     submit handler runs if any cascading option is unfilled.

     Scope note: the .co-option blocks are siblings of the buy-buttons wrapper
     inside .wt-product__info, NOT descendants of it. The old code scoped the
     lookup to the buy-buttons wrapper's parent, so it never matched anything
     and this guard was silently inert. Scoping to .wt-product__info makes it
     actually enforce. If you intend a cascading option to be OPTIONAL, this is
     the one behavior change to be aware of. */

  function attachSubmitGuard() {
    document.querySelectorAll("product-form form").forEach((form) => {
      if (form.dataset.cascadingGuarded === "true") return;
      form.dataset.cascadingGuarded = "true";

      form.addEventListener(
        "submit",
        (e) => {
          const scope = form.closest(".wt-product__info") || document;
          const options = scope.querySelectorAll(".co-option");
          if (!options || options.length === 0) return;

          let hasError = false;
          let firstErrorEl = null;

          options.forEach((option) => {
            option.querySelectorAll(".co-error").forEach((el) => el.remove());

            const radios = option.querySelectorAll('input[type="radio"]');
            const select = option.querySelector("select");

            let filled = false;
            if (radios.length > 0) {
              filled = Array.from(radios).some((r) => r.checked);
            } else if (select) {
              filled = !!select.value;
            }

            if (!filled) {
              const err = document.createElement("div");
              err.className = "co-error";
              err.textContent = "Please make a selection";
              option.appendChild(err);
              hasError = true;
              if (!firstErrorEl) firstErrorEl = err;
            }
          });

          if (hasError) {
            e.preventDefault();
            e.stopImmediatePropagation();
            if (firstErrorEl) {
              firstErrorEl.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }
          }
        },
        true
      );
    });
  }

  /* --------------------------------- Boot --------------------------------- */

  function boot() {
    init(document);
    attachSubmitGuard();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", () => {
    init(document);
    attachSubmitGuard();
  });
})();